// Browser-side image downscale + compress
// Returns a new File with the same name. Falls back to original on any error.
export async function compressImage(file, { maxDim = 1800, quality = 0.85 } = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // GIFs and SVGs: don't touch (we'd lose animation / vector)
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;

  try {
    const bitmap = await loadImage(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);

    const outType = file.type === "image/png" ? "image/jpeg" : file.type;
    const blob = await new Promise((res, rej) =>
      canvas.toBlob(
        (b) => (b ? res(b) : rej(new Error("toBlob failed"))),
        outType,
        quality,
      ),
    );

    // If compression actually made it bigger (rare), keep the original.
    if (blob.size >= file.size) return file;

    const ext = outType === "image/jpeg" ? "jpg" : outType.split("/")[1] || "jpg";
    const base = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.${ext}`, { type: outType, lastModified: Date.now() });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Image compression failed, sending original:", e);
    return file;
  }
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}
